/**
 * Settings Initialization Service
 * Loads and applies company settings (currency, date format, etc.) from backend
 * and stores them in session storage for use across the application
 */

import { setCurrencySettings } from '../utils/currencyFormatter';
import { setDateFormatSettings } from '../utils/dateFormatter';
import companySettingsService from './companySettingsService';

// Session storage keys
const STORAGE_KEYS = {
  COMPANY_SETTINGS: 'company_settings',
  CURRENCY_SETTINGS: 'currency_settings',
  DATE_FORMAT: 'date_format',
  SETTINGS_LOADED: 'settings_loaded_timestamp'
};

/**
 * Initialize and load company settings
 * Call this after user login
 */
export const initializeSettings = async () => {
  try {
    console.log('🔄 [SETTINGS INIT] Starting to fetch company settings...');
    
    // Fetch settings from backend
    const settings = await companySettingsService.getSettings();
    
    console.log('📦 [SETTINGS INIT] Raw settings received from backend:', settings);
    
    if (!settings) {
      console.warn('⚠️ [SETTINGS INIT] No settings received from backend, using defaults');
      applyDefaultSettings();
      return false;
    }

    // Store complete settings in session storage
    sessionStorage.setItem(STORAGE_KEYS.COMPANY_SETTINGS, JSON.stringify(settings));
    sessionStorage.setItem(STORAGE_KEYS.SETTINGS_LOADED, new Date().getTime().toString());

    // Extract and store currency settings
    const currencySettings = {
      currencyCode: settings.currencyCode || 'INR',
      currencySymbol: settings.currencySymbol || '₹',
      decimalPlaces: settings.decimalPlaces || 2,
      thousandSeparator: settings.thousandSeparator || ',',
      decimalSeparator: settings.decimalSeparator || '.',
      languageCode: settings.languageCode || 'en-IN'
    };
    
    console.log('💰 [SETTINGS INIT] Extracted currency settings:', currencySettings);
    
    sessionStorage.setItem(STORAGE_KEYS.CURRENCY_SETTINGS, JSON.stringify(currencySettings));
    
    // Apply currency settings globally
    setCurrencySettings(currencySettings);
    console.log('✅ [SETTINGS INIT] Currency settings applied to global formatter');
    
    // Store date format settings
    const dateFormatSettings = {
      dateFormat: settings.dateFormat || 'dd/MM/yyyy',
      timeFormat: settings.timeFormat || '24',
      timezone: settings.timezone || 'Asia/Kolkata'
    };
    
    sessionStorage.setItem(STORAGE_KEYS.DATE_FORMAT, JSON.stringify(dateFormatSettings));
    
    // Apply date format settings globally
    setDateFormatSettings(dateFormatSettings);
    console.log('✅ [SETTINGS INIT] Date format settings applied to global formatter');
    
    console.log('✅ [SETTINGS INIT] Settings initialized successfully:', {
      currency: currencySettings.currencyCode,
      symbol: currencySettings.currencySymbol,
      dateFormat: dateFormatSettings.dateFormat,
      decimalPlaces: currencySettings.decimalPlaces
    });
    
    // Test the formatter
    console.log('🧪 [SETTINGS INIT] Testing formatter with 1000:', 
      new Intl.NumberFormat(currencySettings.languageCode, {
        style: 'currency',
        currency: currencySettings.currencyCode,
      }).format(1000)
    );
    
    return true;
  } catch (error) {
    console.error('❌ [SETTINGS INIT] Error initializing settings:', error);
    console.error('❌ [SETTINGS INIT] Error stack:', error.stack);
    
    // Apply default settings on error
    applyDefaultSettings();
    return false;
  }
};

/**
 * Apply default settings (fallback)
 */
export const applyDefaultSettings = () => {
  const defaultCurrencySettings = {
    currencyCode: 'INR',
    currencySymbol: '₹',
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.',
    languageCode: 'en-IN'
  };
  
  sessionStorage.setItem(STORAGE_KEYS.CURRENCY_SETTINGS, JSON.stringify(defaultCurrencySettings));
  setCurrencySettings(defaultCurrencySettings);
  
  const defaultDateSettings = {
    dateFormat: 'dd/MM/yyyy',
    timeFormat: '24',
    timezone: 'Asia/Kolkata'
  };
  
  sessionStorage.setItem(STORAGE_KEYS.DATE_FORMAT, JSON.stringify(defaultDateSettings));
  setDateFormatSettings(defaultDateSettings);
  
  console.log('Default settings applied');
};

/**
 * Get currency settings from session storage
 */
export const getCurrencySettingsFromStorage = () => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEYS.CURRENCY_SETTINGS);
    if (stored) {
      const settings = JSON.parse(stored);
      setCurrencySettings(settings); // Re-apply to currency formatter
      return settings;
    }
  } catch (error) {
    console.error('Error loading currency settings from storage:', error);
  }
  
  // Return and apply defaults if not found
  const defaults = {
    currencyCode: 'INR',
    currencySymbol: '₹',
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.',
    languageCode: 'en-IN'
  };
  
  setCurrencySettings(defaults);
  return defaults;
};

/**
 * Get date format settings from session storage
 */
export const getDateFormatFromStorage = () => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEYS.DATE_FORMAT);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error loading date format from storage:', error);
  }
  
  return {
    dateFormat: 'dd/MM/yyyy',
    timeFormat: '24h',
    timezone: 'Asia/Kolkata'
  };
};

/**
 * Get all company settings from session storage
 */
export const getCompanySettingsFromStorage = () => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEYS.COMPANY_SETTINGS);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error loading company settings from storage:', error);
  }
  
  return null;
};

/**
 * Clear all settings from session storage
 * Call this on logout
 */
export const clearSettings = () => {
  sessionStorage.removeItem(STORAGE_KEYS.COMPANY_SETTINGS);
  sessionStorage.removeItem(STORAGE_KEYS.CURRENCY_SETTINGS);
  sessionStorage.removeItem(STORAGE_KEYS.DATE_FORMAT);
  sessionStorage.removeItem(STORAGE_KEYS.SETTINGS_LOADED);
  
  console.log('Settings cleared from session storage');
};

/**
 * Check if settings are loaded
 */
export const areSettingsLoaded = () => {
  return sessionStorage.getItem(STORAGE_KEYS.SETTINGS_LOADED) !== null;
};

/**
 * Force reload settings from backend
 */
export const reloadSettings = async () => {
  clearSettings();
  return await initializeSettings();
};

export default {
  initializeSettings,
  applyDefaultSettings,
  getCurrencySettingsFromStorage,
  getDateFormatFromStorage,
  getCompanySettingsFromStorage,
  clearSettings,
  areSettingsLoaded,
  reloadSettings
};

